import {v4} from 'uuid'
import {call, take, select, put} from '../../lib'
import {doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT} from './identity_refresh_token_submit'
import {AUTH_REFRESH_UNAVAILABLE, AUTH_REFRESHING_FAILURE, AUTH_SET} from '../state/auth'

// Exactly one of these follows every refresh attempt, so a caller that waits on all three
// always hears back. Waiting on AUTH_SET and AUTH_REFRESHING_FAILURE alone is what used to hold
// every request saga for the whole length of an outage.
const REFRESH_OUTCOME_ACTION_LIST = [AUTH_SET, AUTH_REFRESHING_FAILURE, AUTH_REFRESH_UNAVAILABLE]

// if the token expires within this window, refresh before handing it out
const TOKEN_REFRESH_WINDOW_MS = 15000

function* waitForRefreshOutcome() {
  // one outcome ends the wait: a transient one means the token is unchanged and a retry is
  // scheduled, which is the caller's cue to go ahead with what it has
  let isRefreshing: boolean = yield select((state) => state?.auth?.isRefreshing)
  while (isRefreshing) {
    yield take(REFRESH_OUTCOME_ACTION_LIST)
    isRefreshing = yield select((state) => state?.auth?.isRefreshing)
  }
}

// In this order: an attempt in flight is waited out; otherwise a scheduled retry means the
// current token is handed back at once, so user activity can neither defeat the schedule nor be
// blocked by it; otherwise a token that is about to expire is refreshed the usual way.
function* waitForTokenRefresh() {
  const isRefreshing: boolean = yield select((state) => state?.auth?.isRefreshing)
  if (isRefreshing) {
    yield call(waitForRefreshOutcome)
    return
  }

  const refreshUnavailable: boolean = yield select((state) => state?.auth?.refreshUnavailable)
  if (refreshUnavailable) return

  const accessTokenExpireTimestamp: number = (yield select((state) => state?.auth?.accessTokenExpireTimestamp)) ?? 0
  const expireTime = accessTokenExpireTimestamp - new Date().getTime()
  if (expireTime >= TOKEN_REFRESH_WINDOW_MS) return

  const requestId = v4()
  yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({requestId}))

  // the outcome, not AUTH_REFRESHING: under `takeLeading` a dispatch that lands on a running
  // cycle is dropped and no AUTH_REFRESHING follows it
  yield take(REFRESH_OUTCOME_ACTION_LIST)
}

// Saga to get token, waits refreshing finished
export function* getToken() {
  yield call(waitForTokenRefresh)
  const token: string | null = yield select((state) => state?.auth?.accessToken)
  return token
}
